/**
 * @file   src\Pages\LprDeviceListing.tsx
 * @brief  LPR device listing page.
 * @date   JUN, 2026
 */

import { React, useEffect, useState, Container, Table, Button, Modal, Form, useOutletContext, useParams, Pagination } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import AddIcon from '../assets/images/icons/addicon';
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import { CommonMessages } from '../messages/validationMessages';
import { MessageToaster } from '../utils/toastUtil';
import axios from '../config/axiosConfig';
import { EquipmentApi } from '../utils/apiUrls';
import { userType } from '../utils/enums';
import SmallLoader from '../components/SmallLoader';
import copy from "clipboard-copy";

interface LprDevice {
    id: number;
    cameraName: string;
    cameraId: string;
}

interface LprDeviceListingProps {
    userType?: userType;
}

const LprDeviceListing = (props: LprDeviceListingProps) => {
    const { updateData } = useOutletContext<AppContextType>();
    const param = useParams();
    const toaster = new MessageToaster();
    const carwash_owner = props.userType === userType['CARWASH-OWNER'];
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [deviceName, setDeviceName] = useState('');
    const [deviceNameError, setDeviceNameError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
    const [selectedDeleteDeviceId, setSelectedDeleteDeviceId] = useState<number | null>(null);
    const [selectedDeleteDeviceName, setSelectedDeleteDeviceName] = useState('');
    const [devices, setDevices] = useState<LprDevice[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const getDevices = async (pageNo = 1) => {
        try {
            setLoading(true);
            const getLprDevicesUrl = carwash_owner
                ? EquipmentApi.CARWASH_OWNER_GET_LPR_DEVICES
                : `${EquipmentApi.GET_LPR_DEVICES}${param.id}`;
            const { data } = await axios.get(getLprDevicesUrl, {
                params: {
                    pageNo
                }
            });

            if (data?.error === 0) {
                const apiList = data?.data?.list || data?.data || [];
                if (Array.isArray(apiList)) {
                    const transformedList = apiList
                        .map((item: any) => ({
                            id: item?.washBayDeviceId || item?.WashBayDeviceID || item?.washBayDeviceID || item?.washBayDeviceId || 0,
                            cameraName: item?.deviceName || item?.DeviceName || "N/A",
                            cameraId: item?.cameraId || item?.CameraID || item?.cameraID || item?.CameraId || "N/A"
                        }))
                        .filter((item: LprDevice) => item.id !== 0);

                    setDevices(transformedList);
                    const totalCount = data?.data?.totalCount ?? transformedList.length;
                    const limit = (data?.data?.limit ?? transformedList.length) || 1;
                    setTotalPages(Math.ceil(totalCount / limit) || 1);
                } else {
                    setDevices([]);
                    setTotalPages(1);
                }
            } else {
                setDevices([]);
                setTotalPages(1);
                toaster.toastError(data?.message || "Failed to fetch LPR devices.");
            }
        } catch (error: any) {
            setDevices([]);
            setTotalPages(1);
            toaster.toastError(error?.response?.data?.message || error?.message || "Failed to fetch LPR devices.");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(
            1,
            currentPage - Math.floor(totalPageNumbersToShow / 2)
        );
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const handleCopyCameraId = (cameraId: string) => {
        if (!cameraId || cameraId === "N/A") return;
        copy(cameraId);
        toaster.toastSuccess("Camera ID copied to clipboard!");
    };

    const handleOpenAddDeviceModal = () => {
        setDeviceName('');
        setDeviceNameError('');
        setIsEditMode(false);
        setSelectedDeviceId(null);
        setShowAddDeviceModal(true);
    };

    const handleCloseAddDeviceModal = () => {
        setShowAddDeviceModal(false);
        setDeviceName('');
        setDeviceNameError('');
        setIsEditMode(false);
        setSelectedDeviceId(null);
    };

    const handleEditDevice = (device: LprDevice) => {
        setDeviceName(device.cameraName === "N/A" ? '' : device.cameraName);
        setDeviceNameError('');
        setSelectedDeviceId(device.id);
        setIsEditMode(true);
        setShowAddDeviceModal(true);
    };

    const handleOpenDeleteDeviceModal = (deviceId: number, deviceName: string) => {
        setSelectedDeleteDeviceId(deviceId);
        setSelectedDeleteDeviceName(deviceName);
        setShowDeleteDeviceModal(true);
    };

    const handleCloseDeleteDeviceModal = () => {
        setSelectedDeleteDeviceId(null);
        setSelectedDeleteDeviceName('');
        setShowDeleteDeviceModal(false);
    };

    const handleDeleteDevice = async () => {
        if (selectedDeleteDeviceId === null) return;

        try {
            setLoading(true);
            const { data } = await axios.post(EquipmentApi.CARWASH_OWNER_DELETE_LPR_DEVICE, {
                washBayDeviceId: selectedDeleteDeviceId
            });

            if (data?.error === 0) {
                toaster.toastSuccess(data?.message || "Device deleted successfully.");
                handleCloseDeleteDeviceModal();
                await getDevices(currentPage);
            } else {
                toaster.toastError(data?.message || "Failed to delete device.");
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || "Failed to delete device.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDevice = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedDeviceName = deviceName.trim();
        if (!trimmedDeviceName) {
            setDeviceNameError(CommonMessages.camera_name_required);
            return;
        }
        setDeviceNameError('');

        try {
            setLoading(true);
            const requestParams = {
                washBayDeviceId: selectedDeviceId,
                deviceName: trimmedDeviceName
            };
            const { data } = isEditMode
                ? await axios.post(EquipmentApi.CARWASH_OWNER_EDIT_LPR_DEVICE, requestParams)
                : await axios.post(EquipmentApi.CARWASH_OWNER_ADD_LPR_DEVICE, {
                    deviceName: trimmedDeviceName
                });

            if (data?.error === 0) {
                toaster.toastSuccess(data?.message || (isEditMode ? "Device updated successfully." : "Device added successfully."));
                handleCloseAddDeviceModal();
                await getDevices(currentPage);
            } else {
                toaster.toastError(data?.message || (isEditMode ? "Failed to update device." : "Failed to add device."));
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || (isEditMode ? "Failed to update device." : "Failed to add device."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        updateData({
            heading: "Manage LPR Devices",
            url: carwash_owner ? `/${pageURLs.lpr_device_listing}` : `/superadmin/${pageURLs.lpr_device_listing}/${param.id}`,
            showBackButton: false,
            showMainMenu: true,
            activeLink: "lpr-device-listing"
        });
    }, []);

    useEffect(() => {
        getDevices(currentPage);
    }, [currentPage]);

    return (
        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {carwash_owner ? (
                    <div className="manage-header-block d-flex justify-content-end">
                        <Button
                            type="button"
                            title="Add New Device"
                            variant="primary"
                            className="primary-btn d-flex align-items-center"
                            onClick={handleOpenAddDeviceModal}
                        >
                            Add New Device
                            <AddIcon />
                        </Button>
                    </div>
                ) : null}

                <div className="table-title-block d-flex align-items-center justify-content-between">
                    <h5 className='f-bold'>Manage LPR Devices</h5>
                </div>

                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Camera name</th>
                                <th>Camera ID</th>
                                {carwash_owner ? <th>Action</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {devices.length > 0 ? (
                                devices.map((device) => (
                                    <tr key={device.id}>
                                        <td>{device.cameraName}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span>{device.cameraId}</span>
                                                {!carwash_owner ? (
                                                    <Button
                                                        title="Copy Camera ID"
                                                        type="button"
                                                        variant="light"
                                                        className="copy-btn px-2 py-1"
                                                        onClick={() => handleCopyCameraId(device.cameraId)}
                                                    >
                                                        Copy
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </td>
                                        {carwash_owner ? (
                                            <td className='nowrap'>
                                                <div className="active-group d-flex align-items-center">
                                                    <Button title="Edit" variant="light" className="light-btn action-btn" onClick={() => handleEditDevice(device)}><EditIcon /></Button>
                                                    <Button title="Delete" variant="light" className="light-btn action-btn" onClick={() => handleOpenDeleteDeviceModal(device.id, device.cameraName)}><DeleteIcon /></Button>
                                                </div>
                                            </td>
                                        ) : null}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td style={{ textAlign: 'center' }} colSpan={carwash_owner ? 3 : 2}>No data found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    {devices.length ? (
                        <div className="mt-3 d-flex justify-content-end">
                            <Pagination>
                                <Pagination.First
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(1)}
                                />
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                />
                                {getVisiblePageNumbers().map((pageNumber) => (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === currentPage}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                />
                                <Pagination.Last
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(totalPages)}
                                />
                            </Pagination>
                        </div>
                    ) : null}
                </div>
            </Container>
            {carwash_owner ? (
                <>
                    <Modal show={showAddDeviceModal} onHide={handleCloseAddDeviceModal} centered className='add-modal device-modal'>
                        <Modal.Body>
                            <h5 className='f-bold'>{isEditMode ? "Edit Device" : "Add Device"}</h5>
                            <form onSubmit={handleSaveDevice}>
                                <div className="form-group">
                                    <p className="label">Camera name</p>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter camera name"
                                        value={deviceName}
                                        onChange={(event) => {
                                            setDeviceName(event.target.value);
                                            if (event.target.value.trim()) {
                                                setDeviceNameError('');
                                            }
                                        }}
                                        autoFocus
                                    />
                                    {deviceNameError && <span className="error-msg">{deviceNameError}</span>}
                                </div>
                                <div className="btn-sec d-grid">
                                    <Button title="Cancel" variant="secondary" className='secondary-btn' onClick={handleCloseAddDeviceModal}>Cancel</Button>
                                    <Button title="Save" variant="primary" className='primary-btn' type='submit' disabled={loading}>Save</Button>
                                </div>
                            </form>
                        </Modal.Body>
                    </Modal>
                    <Modal
                        show={showDeleteDeviceModal}
                        onHide={handleCloseDeleteDeviceModal}
                        centered
                        className='add-modal'
                    >
                        <Modal.Body className='text-center'>
                            <h5 className='f-bold text-center'>Do you want to delete the device {selectedDeleteDeviceName}?</h5>
                            <div className="btn-sec d-grid">
                                <Button variant="light" className="secondary-btn" onClick={handleCloseDeleteDeviceModal} title='Not Now'>Not Now</Button>
                                <Button variant="primary" className='primary-btn btn-main m-0' onClick={handleDeleteDevice} title='Yes' disabled={loading}>Yes</Button>
                            </div>
                        </Modal.Body>
                    </Modal>
                </>
            ) : null}
            {loading && <SmallLoader />}
        </div>
    )
}

export default LprDeviceListing;
